import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import type {
  GetStoreListQuery,
  DeleteStoreMutation,
  DeleteStoreMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const DELETE_STORE = gql(`
  mutation DeleteStore($storeId: Int!) {
    deleteStore(storeId: $storeId) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

export default function AdminStoreDelete() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [deleteStore] = useMutation<
    DeleteStoreMutation,
    DeleteStoreMutationVariables
  >(DELETE_STORE, {
    onCompleted: (res) => {
      if (res.deleteStore.ok) {
        notify("매장이 성공적으로 삭제되었습니다.");
        setSelectedStore(null);
        refetch();
      } else {
        notifyError(res.deleteStore.error ?? "삭제 중 오류가 발생했습니다.");
      }
    },
    onError: () => {
      notifyError("삭제 중 오류가 발생했습니다.");
    },
  });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const handleConfirmDelete = () => {
    if (!selectedStore) return;
    const isConfirmed = window.confirm(
      `"${selectedStore.title}" 매장을 삭제처리하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
    );
    if (isConfirmed) {
      setIsDeleting(true);
      deleteStore({
        variables: { storeId: selectedStore.id },
      }).finally(() => setIsDeleting(false));
    }
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 삭제처리"
      description="삭제할 매장을 선택한 뒤 삭제처리하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            extraHeaders={
              <th className="px-4 py-2 font-medium text-gray-700">
                가입상태
              </th>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <td className="px-4 py-3">
                  <span
                    className={clsx(
                      "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                      full.signupState === "승인" &&
                        "bg-green-100 text-green-800",
                      full.signupState === "요청" &&
                        "bg-yellow-100 text-yellow-800",
                      full.signupState === "거절" &&
                        "bg-red-100 text-red-800",
                      full.signupState === "요청완료" &&
                        "bg-orange-100 text-orange-800",
                      !["승인", "요청", "거절", "요청완료"].includes(
                        full.signupState ?? ""
                      ) && "bg-gray-100 text-gray-800"
                    )}
                  >
                    {full.signupState ?? "—"}
                  </span>
                </td>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <div className="mt-2">
                  <span
                    className={clsx(
                      "px-2 py-0.5 inline-flex font-semibold rounded-full text-xs",
                      full.signupState === "승인" &&
                        "bg-green-100 text-green-800",
                      full.signupState === "요청" &&
                        "bg-yellow-100 text-yellow-800",
                      full.signupState === "거절" &&
                        "bg-red-100 text-red-800",
                      full.signupState === "요청완료" &&
                        "bg-orange-100 text-orange-800",
                      !["승인", "요청", "거절", "요청완료"].includes(
                        full.signupState ?? ""
                      ) && "bg-gray-100 text-gray-800"
                    )}
                  >
                    {full.signupState ?? "—"}
                  </span>
                </div>
              );
            }}
            emptyText={
              keyword ? "검색 결과가 없습니다." : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "삭제처리",
        emptyText: "매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="flex items-center gap-3">
              {selectedStore.img ? (
                <ResponsiveCDNImage
                  src={selectedStore.img}
                  alt={selectedStore.title}
                  className="w-20 h-20 rounded object-cover"
                  sizeRatio={0.2}
                />
              ) : (
                <div className="w-20 h-20 bg-gray-200 rounded" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {selectedStore.title}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedStore.address ?? "—"}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700 text-sm">
              <div>
                <span className="font-medium text-gray-800">연락처:</span>{" "}
                {selectedStore.seller?.contactNumber ?? "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">가입일:</span>{" "}
                {new Date(selectedStore.createdAt).toLocaleDateString()}
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              이 작업은 되돌릴 수 없습니다.
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSelectedStore(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200"
              >
                선택 해제
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinnerInline />
                    삭제 중...
                  </>
                ) : (
                  "삭제처리"
                )}
              </button>
            </div>
          </div>
        ) : null,
      }}
    />
  );
}
